import type { AuthResponse, AuthUser } from '@/api/types';

const STORAGE_KEY = 'anyware.controlplane.session.v1';

export interface Session {
  accessToken: string;
  accessTokenExpiresAt: string;
  refreshToken: string;
  user: AuthUser;
}

type Listener = (session: Session | null) => void;

const listeners = new Set<Listener>();
let current: Session | null = read();

function read(): Session | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Session) : null;
  } catch {
    // Corrupt or unavailable storage must read as "logged out", never throw at boot.
    return null;
  }
}

function emit() {
  for (const listener of listeners) listener(current);
}

export function getSession(): Session | null {
  return current;
}

export function setSession(auth: AuthResponse): void {
  current = {
    accessToken: auth.access_token,
    accessTokenExpiresAt: auth.access_token_expires_at,
    refreshToken: auth.refresh_token,
    user: auth.user,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(current));
  emit();
}

export function clearSession(): void {
  current = null;
  localStorage.removeItem(STORAGE_KEY);
  emit();
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function hasPermission(permission: string): boolean {
  return current?.user.permissions.includes(permission) ?? false;
}

/**
 * True when the access token is expired or within 30s of it. The API client refreshes
 * proactively on this so a request never fails purely because it raced the expiry.
 */
export function accessTokenIsStale(): boolean {
  if (!current) return false;
  const expiry = Date.parse(current.accessTokenExpiresAt);
  return Number.isNaN(expiry) || expiry - Date.now() < 30_000;
}
